'use client';
import {useRouter} from 'next/navigation';
import {useTranslation} from 'react-i18next';
import {
  trackHomepageCardClick,
  trackHomepageRegisterInterestClick,
  trackHomepageViewAllClick,
  trackHomepageViewItemClick,
} from '../../analytics';
import {PROJECTS_EMPTY} from '../../content';
import {openRegisterInterest} from '../register-interest';
import {CarouselSection} from './CarouselSection';
import {ListingCard} from './ListingCard';
import {LOCATION_ICON} from './meta-icons';

const SECTION_NAME = 'featured_projects';

export interface ProjectCardItem {
  uuid: string;
  title: string;
  image: string;
  location: string;
  href: string;
}

export interface FeaturedProjectsCarouselProps {
  projects: ProjectCardItem[];
  viewAllHref: string;
  title?: string;
  sectionId?: string;
}

export function FeaturedProjectsCarousel({projects, viewAllHref, title, sectionId}: FeaturedProjectsCarouselProps) {
  const {t} = useTranslation();
  const router = useRouter();
  const section = {name: SECTION_NAME, id: sectionId};

  return (
    <CarouselSection
      title={title ?? t('featured_projects_title')}
      subtitle={t('featured_projects_subtitle')}
      titleMobileSize="title-5"
      empty={PROJECTS_EMPTY}
      viewAll={{
        label: t('view_all_projects'),
        href: viewAllHref,
        onClick: () => {
          trackHomepageViewAllClick(section);
        },
      }}
    >
      {projects.map(project => (
        <ListingCard
          deferImage
          key={project.uuid}
          title={project.title}
          image={project.image}
          href={project.href}
          primaryMeta={project.location ? {icon: LOCATION_ICON, label: project.location} : undefined}
          onClick={() => {
            trackHomepageCardClick({id: project.uuid, name: project.title}, section);
          }}
          hoverActions={[
            {
              label: t('register_interest'),
              onClick: () => {
                trackHomepageRegisterInterestClick({id: project.uuid, name: project.title}, section);
                openRegisterInterest({projectId: project.uuid});
              },
            },
            {
              label: t('view_project'),
              onClick: () => {
                trackHomepageViewItemClick({id: project.uuid, name: project.title}, section);
                router.push(project.href);
              },
            },
          ]}
        />
      ))}
    </CarouselSection>
  );
}

FeaturedProjectsCarousel.displayName = 'FeaturedProjectsCarousel';
